import {
  EventTypesResponse,
  LogEntryList,
  LogEntryType,
  LogUserOptions,
  SystemLoggingStats,
  TrendsResponse,
} from '../types/SystemLogging.types';
import API from './ApiBase';
import { getFilenameFromContentDisposition } from '../utils/File.util';

export type BlobDownload = {
  blob: Blob;
  filename?: string;
};

type GetTrendsParams = {
  aiSystemId: string;
  months: number;
};

type GetEventsParams = {
  aiSystemId?: string;
  type?: LogEntryType;
  types?: LogEntryType[];
  eventType?: string;
  severity?: 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
  severities?: string[];
  changeImpact?: string;
  changeImpacts?: string[];
  userId?: string;
  userIds?: string[];
  githubUserName?: string;
  githubUserNames?: string[];
  action?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

export const getTrends = async (
  params: GetTrendsParams
): Promise<TrendsResponse> => {
  try {
    const searchParams = new URLSearchParams();
    searchParams.append('aiSystemId', params.aiSystemId);
    searchParams.append('months', params.months.toString());

    const queryString = searchParams.toString();
    const url = `/system-logging/trends?${queryString}`;

    const response = await API.get(url);

    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch trends data'
    );
  }
};

export const getEvents = async (
  params: GetEventsParams = {}
): Promise<LogEntryList> => {
  try {
    const searchParams = new URLSearchParams();

    if (params.aiSystemId) searchParams.append('aiSystemId', params.aiSystemId);
    if (params.type) searchParams.append('type', params.type);
    if (params.types?.length) {
      params.types.forEach((type) => searchParams.append('types', type));
    }
    if (params.eventType) searchParams.append('eventType', params.eventType);
    if (params.severity) searchParams.append('severity', params.severity);
    if (params.severities?.length) {
      params.severities.forEach((severity) =>
        searchParams.append('severities', severity)
      );
    }
    if (params.changeImpact)
      searchParams.append('changeImpact', params.changeImpact);
    if (params.changeImpacts?.length) {
      params.changeImpacts.forEach((impact) =>
        searchParams.append('changeImpacts', impact)
      );
    }
    if (params.userId) searchParams.append('userId', params.userId);
    if (params.userIds?.length) {
      params.userIds.forEach((id) => searchParams.append('userIds', id));
    }
    if (params.githubUserName)
      searchParams.append('githubUserName', params.githubUserName);
    if (params.githubUserNames?.length) {
      params.githubUserNames.forEach((name) =>
        searchParams.append('githubUserNames', name)
      );
    }
    if (params.action) searchParams.append('action', params.action);
    if (params.search) searchParams.append('search', params.search);
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);
    if (params.limit !== undefined)
      searchParams.append('limit', params.limit.toString());
    if (params.offset !== undefined)
      searchParams.append('offset', params.offset.toString());
    if (params.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);

    const queryString = searchParams.toString();
    const url = `/system-logging/events${queryString ? `?${queryString}` : ''}`;

    const response = await API.get(url);

    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch events data'
    );
  }
};

export type GetAuditLogsParams = {
  aiSystemId?: string;
  aiSystemIds?: string[];
  type?: LogEntryType;
  types?: LogEntryType[];
  eventType?: string;
  severity?: 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
  severities?: string[];
  changeImpact?: string;
  changeImpacts?: string[];
  userId?: string;
  userIds?: string[];
  githubUserName?: string;
  githubUserNames?: string[];
  search?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

const buildAuditLogsSearchParams = (params: GetAuditLogsParams): URLSearchParams => {
  const searchParams = new URLSearchParams();

  if (params.aiSystemId) searchParams.append('aiSystemId', params.aiSystemId);
  if (params.aiSystemIds?.length) {
    params.aiSystemIds.forEach((id) => searchParams.append('aiSystemIds', id));
  }
  if (params.type) searchParams.append('type', params.type);
  if (params.types?.length) {
    params.types.forEach((type) => searchParams.append('types', type));
  }
  if (params.eventType) searchParams.append('eventType', params.eventType);
  if (params.severity) searchParams.append('severity', params.severity);
  if (params.severities?.length) {
    params.severities.forEach((severity) =>
      searchParams.append('severities', severity)
    );
  }
  if (params.changeImpact)
    searchParams.append('changeImpact', params.changeImpact);
  if (params.changeImpacts?.length) {
    params.changeImpacts.forEach((impact) =>
      searchParams.append('changeImpacts', impact)
    );
  }
  if (params.userId) searchParams.append('userId', params.userId);
  if (params.userIds?.length) {
    params.userIds.forEach((id) => searchParams.append('userIds', id));
  }
  if (params.githubUserName)
    searchParams.append('githubUserName', params.githubUserName);
  if (params.githubUserNames?.length) {
    params.githubUserNames.forEach((name) =>
      searchParams.append('githubUserNames', name)
    );
  }
  if (params.search) searchParams.append('search', params.search);
  if (params.startDate) searchParams.append('startDate', params.startDate);
  if (params.endDate) searchParams.append('endDate', params.endDate);
  if (params.limit !== undefined)
    searchParams.append('limit', params.limit.toString());
  if (params.offset !== undefined)
    searchParams.append('offset', params.offset.toString());
  if (params.sortBy) searchParams.append('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);

  return searchParams;
};

export const getAuditLogs = async (
  params: GetAuditLogsParams = {}
): Promise<LogEntryList> => {
  try {
    const queryString = buildAuditLogsSearchParams(params).toString();
    const url = `/system-logging/audit-logs${queryString ? `?${queryString}` : ''}`;

    const response = await API.get(url);

    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch audit logs data'
    );
  }
};

const exportOrganizationLogs = async (
  format: 'csv' | 'json' | 'pdf',
  params: GetAuditLogsParams,
  errorMessage: string
): Promise<BlobDownload> => {
  try {
    const queryString = buildAuditLogsSearchParams(params).toString();
    const url = `/system-logging/audit-logs/export/${format}${
      queryString ? `?${queryString}` : ''
    }`;

    const response = await API.get(url, { responseType: 'blob' });
    return {
      blob: response.data,
      filename: getFilenameFromContentDisposition(
        response.headers['content-disposition']
      ),
    };
  } catch (error: any) {
    throw new Error(error.response?.data?.message || errorMessage);
  }
};

export const exportOrganizationLogsCSV = (
  params: GetAuditLogsParams = {}
): Promise<BlobDownload> =>
  exportOrganizationLogs('csv', params, 'Failed to export audit log as CSV');

export const exportOrganizationLogsJSON = (
  params: GetAuditLogsParams = {}
): Promise<BlobDownload> =>
  exportOrganizationLogs('json', params, 'Failed to export audit log as JSON');

export const exportOrganizationLogsPDF = (
  params: GetAuditLogsParams = {}
): Promise<BlobDownload> =>
  exportOrganizationLogs('pdf', params, 'Failed to export audit log as PDF');

// SUPER_ADMIN only (enforced server-side) - the underlying hash chain is
// platform-wide, not scoped to one organization, so this report can't be
// meaningfully offered to a regular ADMIN. Takes no filters - it's a
// verification result, not a filtered log view.
export const downloadTamperEvidenceReport = async (): Promise<BlobDownload> => {
  try {
    const response = await API.get(
      '/system-logging/audit-logs/tamper-evidence-report',
      { responseType: 'blob' }
    );
    return {
      blob: response.data,
      filename: getFilenameFromContentDisposition(
        response.headers['content-disposition']
      ),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        'Failed to generate tamper-evidence verification report'
    );
  }
};

export const getEventsCount = async (
  aiSystemId: string,
  type?: LogEntryType
): Promise<number> => {
  try {
    const response = await getEvents({ aiSystemId, type, limit: 20, offset: 0 });
    return response.total || 0;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch events count'
    );
  }
};

export const getEventTypes = async (): Promise<EventTypesResponse> => {
  try {
    const response = await API.get<EventTypesResponse>(
      '/system-logging/event-types'
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch event types'
    );
  }
};

export const exportEventsCSV = async (
  params: GetEventsParams = {}
): Promise<BlobDownload> => {
  try {
    const searchParams = new URLSearchParams();

    if (params.aiSystemId) searchParams.append('aiSystemId', params.aiSystemId);
    if (params.types?.length) {
      params.types.forEach((type) => searchParams.append('types', type));
    }
    if (params.eventType) searchParams.append('eventType', params.eventType);
    if (params.severity) searchParams.append('severity', params.severity);
    if (params.severities?.length) {
      params.severities.forEach((severity) =>
        searchParams.append('severities', severity)
      );
    }
    if (params.changeImpact)
      searchParams.append('changeImpact', params.changeImpact);
    if (params.userId) searchParams.append('userId', params.userId);
    if (params.userIds?.length) {
      params.userIds.forEach((id) => searchParams.append('userIds', id));
    }
    if (params.githubUserName)
      searchParams.append('githubUserName', params.githubUserName);
    if (params.githubUserNames?.length) {
      params.githubUserNames.forEach((name) =>
        searchParams.append('githubUserNames', name)
      );
    }
    if (params.action) searchParams.append('action', params.action);
    if (params.search) searchParams.append('search', params.search);
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);

    const queryString = searchParams.toString();
    const url = `/system-logging/events/export/csv${queryString ? `?${queryString}` : ''}`;

    const response = await API.get(url, {
      responseType: 'blob',
    });

    return {
      blob: response.data,
      filename: getFilenameFromContentDisposition(
        response.headers['content-disposition']
      ),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to export events CSV'
    );
  }
};

export const exportEventsJSON = async (
  params: GetEventsParams = {}
): Promise<BlobDownload> => {
  try {
    const searchParams = new URLSearchParams();

    if (params.aiSystemId) searchParams.append('aiSystemId', params.aiSystemId);
    if (params.types?.length) {
      params.types.forEach((type) => searchParams.append('types', type));
    }
    if (params.eventType) searchParams.append('eventType', params.eventType);
    if (params.severity) searchParams.append('severity', params.severity);
    if (params.severities?.length) {
      params.severities.forEach((severity) =>
        searchParams.append('severities', severity)
      );
    }
    if (params.changeImpact)
      searchParams.append('changeImpact', params.changeImpact);
    if (params.userId) searchParams.append('userId', params.userId);
    if (params.userIds?.length) {
      params.userIds.forEach((id) => searchParams.append('userIds', id));
    }
    if (params.githubUserName)
      searchParams.append('githubUserName', params.githubUserName);
    if (params.githubUserNames?.length) {
      params.githubUserNames.forEach((name) =>
        searchParams.append('githubUserNames', name)
      );
    }
    if (params.action) searchParams.append('action', params.action);
    if (params.search) searchParams.append('search', params.search);
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);

    const queryString = searchParams.toString();
    const url = `/system-logging/events/export/json${queryString ? `?${queryString}` : ''}`;

    const response = await API.get(url, {
      responseType: 'blob',
    });

    return {
      blob: response.data,
      filename: getFilenameFromContentDisposition(
        response.headers['content-disposition']
      ),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to export events JSON'
    );
  }
};

export const exportEventsPDF = async (
  params: GetEventsParams = {}
): Promise<BlobDownload> => {
  try {
    const searchParams = new URLSearchParams();

    if (params.aiSystemId) searchParams.append('aiSystemId', params.aiSystemId);
    if (params.types?.length) {
      params.types.forEach((type) => searchParams.append('types', type));
    }
    if (params.eventType) searchParams.append('eventType', params.eventType);
    if (params.severity) searchParams.append('severity', params.severity);
    if (params.severities?.length) {
      params.severities.forEach((severity) =>
        searchParams.append('severities', severity)
      );
    }
    if (params.changeImpact)
      searchParams.append('changeImpact', params.changeImpact);
    if (params.userId) searchParams.append('userId', params.userId);
    if (params.userIds?.length) {
      params.userIds.forEach((id) => searchParams.append('userIds', id));
    }
    if (params.githubUserName)
      searchParams.append('githubUserName', params.githubUserName);
    if (params.githubUserNames?.length) {
      params.githubUserNames.forEach((name) =>
        searchParams.append('githubUserNames', name)
      );
    }
    if (params.action) searchParams.append('action', params.action);
    if (params.search) searchParams.append('search', params.search);
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);

    const queryString = searchParams.toString();
    const url = `/system-logging/events/export/pdf${queryString ? `?${queryString}` : ''}`;

    const response = await API.get(url, {
      responseType: 'blob',
    });

    return {
      blob: response.data,
      filename: getFilenameFromContentDisposition(
        response.headers['content-disposition']
      ),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to export events PDF'
    );
  }
};

export const getSystemLoggingStats = async (
  aiSystemId?: string
): Promise<SystemLoggingStats> => {
  try {
    const searchParams = new URLSearchParams();
    if (aiSystemId) searchParams.append('aiSystemId', aiSystemId);

    const queryString = searchParams.toString();
    const url = `/system-logging/stats${queryString ? `?${queryString}` : ''}`;

    const response = await API.get<SystemLoggingStats>(url);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch system logging stats'
    );
  }
};

// Populates the "User" filter for this AI system's log view - both platform
// users seen on audit events and GitHub usernames seen on repo syncs.
export const getAiSystemLogUsers = async (
  aiSystemId: string
): Promise<LogUserOptions> => {
  try {
    const response = await API.get<LogUserOptions>(
      `/system-logging/ai-systems/${aiSystemId}/users`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch AI system log users'
    );
  }
};

// Same shape as getAiSystemLogUsers, but org-wide - populates the "User"
// filter on the org-wide audit log table.
export const getOrganizationLogUsers = async (): Promise<LogUserOptions> => {
  try {
    const response = await API.get<LogUserOptions>(
      '/system-logging/audit-logs/users'
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch organization log users'
    );
  }
};

export const updateEventChangeImpact = async (
  eventId: string,
  changeImpact: string,
  reason?: string
): Promise<void> => {
  try {
    const body: { changeImpact: string; reason?: string } = {
      changeImpact,
    };

    if (reason) {
      body.reason = reason;
    }

    await API.put(`/system-logging/events/${eventId}/change-impact`, body);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to update change impact'
    );
  }
};
